import React from 'react';
import { Helmet } from '@vuer-ai/react-helmet-async';
import { LegalPageLayout } from './LegalPageLayout';

const SUPPORT_EMAIL = 'support@studiovaultbeta.com';

export const TermsPage: React.FC = () => (
  <>
    <Helmet>
      <title>Terms of Service — StudioVault</title>
      <meta
        name="description"
        content="Terms governing the StudioVault website, desktop application, and cloud service."
      />
    </Helmet>
    <LegalPageLayout
      dateLabel="UPDATED OCTOBER 13, 2026"
      eyebrow="TERMS"
      title="Clear terms for shared work."
      summary="These Terms govern your use of StudioVault's website, desktop application, and cloud service. Please read them before using the service."
    >
      <section>
        <h2>1. Agreement and eligibility</h2>
        <p>
          By creating an account, downloading the application, or using
          StudioVault, you agree to these Terms and the Privacy Policy. If you
          use StudioVault for an organization, you confirm that you have
          authority to bind that organization.
        </p>
        <p>
          You must be at least 16 years old and legally able to enter into this
          agreement. If local law requires a higher age or parental consent,
          that requirement applies. Do not use the service where doing so would
          be unlawful.
        </p>
      </section>

      <section>
        <h2>2. The service</h2>
        <p>
          StudioVault provides tools for storing, transferring, versioning,
          organizing, and collaborating on music-production projects. Features
          may differ by device, operating system, location, release channel, or
          plan. We may improve, add, remove, or discontinue features, and we
          will provide reasonable notice when a material change adversely
          affects an active paid service.
        </p>
        <p>
          StudioVault may be offered as beta or pre-release software. Beta
          features may be incomplete, change without notice, or contain errors.
          Do not rely on StudioVault as the only copy of important work.
        </p>
      </section>

      <section>
        <h2>3. Accounts and security</h2>
        <p>
          Provide accurate account information and keep it current. You are
          responsible for activity under your account and for protecting your
          device and sign-in credentials. Tell us promptly if you believe your
          account or a shared project has been accessed without authorization.
        </p>
        <p>
          You may not transfer an account, impersonate another person, or use
          another person&apos;s credentials without permission.
        </p>
      </section>

      <section>
        <h2>4. Your content</h2>
        <p>
          You retain ownership of the files, audio, project data, names, notes,
          and other material you upload or create through StudioVault
          (&quot;Content&quot;). These Terms do not transfer ownership of your
          Content to StudioVault.
        </p>
        <p>
          You give StudioVault a worldwide, non-exclusive license to host, copy,
          process, transmit, and display Content only as needed to operate,
          secure, and support the service and to make it available to people you
          authorize. This license ends when the Content is deleted from our
          active systems, subject to reasonable backup cycles and legal
          retention requirements.
        </p>
        <p>
          You confirm that you have the rights needed to upload Content and to
          invite others to access it. You control collaborator access and are
          responsible for choosing appropriate permissions. Removing access does
          not retrieve copies another authorized user already downloaded.
        </p>
      </section>

      <section>
        <h2>4a. Public community contributions</h2>
        <p>
          The community library is an open source library of community-driven
          tools. Publishing there is a separate choice from uploading a private
          project. The{' '}
          <a href="/community-guidelines">
            Community guidelines and copyright policy
          </a>{' '}
          form part of these Terms for community sharing.
        </p>
        <p>
          When you explicitly accept the community sharing terms, you apply CC0
          1.0 Universal to your contribution and its attachments to the extent
          of your rights. That public dedication, including its fallback
          license, survives deletion to the extent permitted by law; the
          deletion rule in Section 4 does not revoke it. Existing uploads are
          not retroactively dedicated without consent.
        </p>
        <p>
          Do not redistribute purchased or unauthorized presets, packs, factory
          content, or other creators’ tools. Original work made with paid
          software is allowed when you have the necessary rights. Confirmed
          violations can result in removal and an account blacklist from
          community sharing. Repeat infringers are subject to termination of
          community-sharing privileges. Decisions can be appealed through the
          community policy’s contact process.
        </p>
      </section>

      <section>
        <h2>5. Acceptable use</h2>
        <p>You may not use StudioVault to:</p>
        <ul>
          <li>violate law or another person&apos;s rights;</li>
          <li>
            upload malware, unlawfully obtained material, or Content you do not
            have permission to use;
          </li>
          <li>
            probe, bypass, or interfere with security, access controls, rate
            limits, or service operation;
          </li>
          <li>access accounts, projects, or systems without authorization;</li>
          <li>
            use automated means that impose an unreasonable load or scrape the
            service except through an interface we provide; or
          </li>
          <li>
            resell, sublicense, or provide the service to third parties unless
            we agree in writing.
          </li>
        </ul>
        <p>
          We may investigate suspected misuse and take proportionate steps to
          protect users and the service.
        </p>
      </section>

      <section>
        <h2>6. Sync, versions, and backups</h2>
        <p>
          File synchronization depends on your device, network, available
          storage, project structure, and third-party systems. Confirm that an
          upload or restore completed before deleting a local copy. Conflicts,
          unsupported files, interrupted transfers, and local application
          behavior can affect a version.
        </p>
        <p>
          StudioVault is a collaboration and versioning tool, not an archival
          guarantee. Keep an independent, tested backup of work you cannot
          replace.
        </p>
      </section>

      <section>
        <h2>7. StudioVault software and intellectual property</h2>
        <p>
          StudioVault and its software, design, branding, and documentation are
          protected by intellectual-property laws. Subject to these Terms, we
          grant you a limited, revocable, non-exclusive, non-transferable right
          to install and use the application for its intended purpose.
        </p>
        <p>
          You may not copy, sell, lease, or distribute the service, or reverse
          engineer it except to the extent a restriction is prohibited by
          applicable law. Open-source components remain subject to their own
          licenses.
        </p>
      </section>

      <section>
        <h2>8. Third-party services</h2>
        <p>
          The service relies on third parties for functions such as sign-in,
          hosting, storage, and application delivery. Their services may be
          governed by separate terms and may become unavailable. References to
          digital audio workstations, plug-ins, and other products do not imply
          affiliation or endorsement.
        </p>
      </section>

      <section>
        <h2>9. Plans and payment</h2>
        <p>
          If we offer paid plans, the price, billing period, renewal terms,
          taxes, storage limits, and cancellation options shown at purchase will
          form part of these Terms. We will not charge you without your
          authorization. Mandatory refund and cancellation rights in your
          location remain unaffected.
        </p>
      </section>

      <section>
        <h2>10. Suspension and ending use</h2>
        <p>
          You may stop using StudioVault at any time and may request account
          deletion by contacting us. We may limit or suspend access when
          reasonably necessary to address a security risk, prevent harm,
          investigate misuse, comply with law, or respond to a material breach
          of these Terms. Where practical, we will explain the reason and give
          you an opportunity to resolve the issue.
        </p>
        <p>
          Before ending use, export Content you wish to keep. After account
          closure, Content may no longer be available, subject to applicable law
          and the retention described in the Privacy Policy.
        </p>
      </section>

      <section>
        <h2>11. Warranties</h2>
        <p>
          We provide the service using reasonable care and skill. Except for
          rights and warranties that cannot lawfully be excluded, StudioVault is
          provided &quot;as is&quot; and &quot;as available.&quot; We do not
          promise uninterrupted operation, that every file or application will
          be compatible, or that the service will prevent every loss or security
          incident.
        </p>
      </section>

      <section>
        <h2>12. Liability</h2>
        <p>
          Nothing in these Terms excludes liability that cannot be excluded
          under applicable law, including applicable consumer guarantees. To the
          maximum extent permitted by law, StudioVault is not liable for
          indirect, incidental, special, punitive, or consequential loss, or for
          lost profits, revenue, goodwill, or data.
        </p>
        <p>
          To the maximum extent permitted by law, StudioVault&apos;s aggregate
          liability arising from the service is limited to the greater of the
          amount you paid StudioVault for the service during the 12 months
          before the event giving rise to the claim or US$100. This limitation
          does not apply where applicable law requires a different result.
        </p>
      </section>

      <section>
        <h2>13. Global availability and export rules</h2>
        <p>
          You are responsible for following laws that apply to your use and
          Content, including intellectual-property, privacy, import, export, and
          sanctions laws. You may not use the service on behalf of a prohibited
          party or in a prohibited location. Availability in a country does not
          mean every feature or commercial offering is available there.
        </p>
      </section>

      <section>
        <h2>14. Changes to these Terms</h2>
        <p>
          We may update these Terms to reflect service, business, or legal
          changes. We will post the revised Terms with a new effective date and
          provide additional notice before material changes take effect where
          required. If you do not agree to revised Terms, stop using the service
          before they take effect.
        </p>
      </section>

      <section>
        <h2>15. Applicable law and disputes</h2>
        <p>
          These Terms are governed by the laws applicable to the StudioVault
          operator, without regard to conflict-of-law principles. If you are a
          consumer, this choice does not remove mandatory rights or access to
          courts available under the law where you live. Before filing a claim,
          please contact us so we can try to resolve the issue informally.
        </p>
      </section>

      <section>
        <h2>16. Contact</h2>
        <p>
          Questions about these Terms can be sent to{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </section>
    </LegalPageLayout>
  </>
);

export default TermsPage;
